<template>
  <div class="input-container" :class="`size-${props.size} ${inputLen === props.length && isFocus ? 'is-full-edit' : ''}`" ref="inputContainer">
    <div class="span-container" @click="refCodeInput.focus()">
      <span v-for="i in props.length" :class="{ 'is-focus': isFocus && inputLen + 1 === i, 'is-input': inputLen >= i, 'is-last': i === props.length, 'disabled': i > inputLen + 1 }" :key="i">{{ inputLen >= i ? inputValText[i - 1] : '' }}</span>
    </div>
    <input v-model="inputVal" name="sms-code" ref="refCodeInput" :disabled="props.disabled || inputDisabled" @keydown="handleKeyDown" @focus="handleFocus" @blur="isFocus = false" type="text" />
  </div>
</template>

<script setup>
import { inputAutofocus } from '@/libs/tools'
import { ElMessage } from 'element-plus'
import { computed, onMounted, onUnmounted, getCurrentInstance, ref, watch, nextTick } from 'vue'

const { $t } = getCurrentInstance().proxy
const props = defineProps({
  // 尺寸：default=>中，small=>小
  size: {
    type: String,
    default: 'default',
  },
  // 验证码长度
  length: {
    type: Number,
    default: 4,
  },
  // 禁用时无法输入
  disabled: {
    type: Boolean,
    default: false,
  },
})
const emits = defineEmits(['on-ok'])
const inputVal = ref('')
const inputDisabled = ref(false)
const inputValText = computed(() => inputVal.value.toString())
const inputLen = computed(() => inputValText.value.length)
const isFocus = ref(false)
const refCodeInput = ref(null)
// 监听最多只能输入props.length位，位数够后失焦并回调on-ok
watch(
  () => inputLen.value,
  newVal => {
    if (newVal > props.length) inputVal.value = inputValText.value.slice(0, 4)
    if (newVal >= props.length) {
      refCodeInput.value.blur()
      inputDisabled.value = true
      // 验证码不会以0开头，如果是则直接报错并清空
      if (inputValText.value.startsWith('0') || !/^\d+$/.test(inputValText.value)) {
        ElMessage.error($t('http_error_2002'))
        clearCode()
        return
      }
      // 后端接受int类型
      emits('on-ok', parseInt(inputValText.value))
    }
  }
)

// 清空验证码
const clearCode = () => {
  inputDisabled.value = false
  nextTick(() => {
    inputVal.value = ''
    focus()
  })
}
// 限制只能输入数字，number输入框还是可以输入字母E/e的
const handleKeyDown = e => {
  // 如果是粘贴，则不拦截，让走paste事件
  const inputKey = (e?.key || '').toLocaleLowerCase()
  if (['backspace'].includes(inputKey) || ((e?.metaKey || e?.ctrlKey) && inputKey === 'v')) {
    return true
  }
  if (!/^\d$/.test(inputKey)) {
    e.preventDefault(); // 阻止链接的默认行为
    return false
  }
}
const handleFocus = (event) => {
  isFocus.value = true
}
// 粘贴验证码
const pasteCode = e => {
  const clipdata = (e.clipboardData || window.clipboardData).getData('text/plain')?.trim()
  event.preventDefault()
  // 限制只能输入数字
  if (!/^\d+$/.test(clipdata)) {
    ElMessage.error($t('Verification code error'))
  } else {
    inputVal.value = clipdata.slice(0, 4)
  }
  return false
}
// 输入框聚焦
const focus = () => {
  inputAutofocus('sms-code')
}
onMounted(() => {
  focus()
  window.addEventListener('paste', pasteCode, { passive: false })
})
onUnmounted(() => {
  // 卸载时需要清除粘贴事件绑定，否则会影响其他输入框的粘贴行为
  window.removeEventListener('paste', pasteCode)
})
// 通过该方式暴露出去的方法才能被父组件访问
defineExpose({
  clearCode,
  focus
})
</script>

<style scoped lang="scss">
.input-container {
  width: 100%;
  position: relative;
  cursor: text;
  &.size-small {
    .span-container {
      span {
        width: 40px;
        height: 40px;
        line-height: 40px;
        font-size: 20px;
      }
    }
  }
  .span-container {
    position: relative;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 1;
    span {
      display: block;
      height: 46px;
      line-height: 46px;
      width: 46px;
      position: relative;
      font-size: 24px;
      text-align: center;
      border: 1.5px solid var(--BG-3, #DDDDDD);
      border-radius: 10px;
      color: var(--BG-10, #222222);
      font-size: 24px;
      box-sizing: border-box;
      &::before {
        content: '';
        display: none;
        height: 24px;
        width: 2px;
        background: var(--BG-10, #222222);
        z-index: 1;
        left: 47%;
        top: calc((100% - 24px) / 2);
        position: absolute;
        animation: cursor-text 1s infinite ease-in-out;
      }
      &.is-focus {
        border: 2px solid var(--Color-1, #7479FF);
        &::before {
          display: block;
        }
      }
      &.disabled {
        background: var(--BG-1normal, #F5F5F5);
      }
      &.is-input::after {
        display: none;
      }
    }
  }
  &.is-full-edit {
    span.is-last {
      // 输入到最后一位的时候光标定位到最右侧
      &::before {
        display: block;
        left: auto;
        right: 0;
      }
    }
  }
  input {
    opacity: 0;
    font-size: 24px;
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 1;
    left: 0;
    top: 0;
  }
}
@keyframes cursor-text {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
@media screen and (max-width: 480px) {
  .input-container {
    max-width: 80% !important;
    .span-container {
      span {
        width: 40px;
        height: 40px;
      }
    }
  }
}
</style>
